import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, MessageSquare, Ticket, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { taka } from "@/lib/api";
import { Field, inputCls } from "@/pages/admin/AdminUI";
import { DISTRICTS } from "@/lib/bd";

const PMS = [["cod", "ক্যাশ অন ডেলিভারি"], ["bkash", "বিকাশ"], ["nagad", "নগদ"], ["bank", "ব্যাংক ট্রান্সফার"]];

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [smsLogs, setSmsLogs] = useState([]);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm({ coupons: [], ...r.data }));
    api.get("/admin/sms-logs").then((r) => setSmsLogs(r.data)).catch(() => {});
  }, []);
  if (!form) return <div className="text-stone-500">লোড হচ্ছে...</div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePM = (pm) => {
    const cur = form.payment_methods || [];
    set("payment_methods", cur.includes(pm) ? cur.filter((x) => x !== pm) : [...cur, pm]);
  };

  const coupons = form.coupons || [];
  const setCoupon = (i, k, v) => set("coupons", coupons.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const addCoupon = () => set("coupons", [...coupons, { code: "", percent: 10, min_amount: 0, active: true }]);
  const delCoupon = (i) => set("coupons", coupons.filter((_, idx) => idx !== i));

  const areas = form.area_delivery || [];
  const setArea = (i, k, v) => set("area_delivery", areas.map((a, idx) => idx === i ? { ...a, [k]: v, ...(k === "district" ? { upazila: "" } : {}) } : a));
  const addArea = () => set("area_delivery", [...areas, { district: "", upazila: "", charge: 60 }]);
  const delArea = (i) => set("area_delivery", areas.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        business_name: form.business_name, logo: form.logo, phone: form.phone,
        phone2: form.phone2, phone3: form.phone3, tagline: form.tagline,
        whatsapp: form.whatsapp, facebook: form.facebook, address: form.address,
        business_description: form.business_description, delivery_info: form.delivery_info,
        bkash_number: form.bkash_number, nagad_number: form.nagad_number, bank_info: form.bank_info,
        delivery_charge: Number(form.delivery_charge), min_order: Number(form.min_order),
        currency: form.currency, payment_methods: form.payment_methods,
        area_delivery: (form.area_delivery || []).filter((a) => a.district).map((a) => ({ district: a.district, upazila: a.upazila || "", charge: Number(a.charge) || 0 })),
        coupons: coupons.filter((c) => c.code).map((c) => ({ code: c.code.toUpperCase(), percent: Number(c.percent) || 0, min_amount: Number(c.min_amount) || 0, active: c.active !== false })),
        sms_enabled: form.sms_enabled !== false,
        sms_sender_id: form.sms_sender_id || "MDRICEMILL",
        sms_provider: form.sms_provider || "mock",
      });
      toast.success("সেটিংস সংরক্ষিত হয়েছে");
    } catch { toast.error("ত্রুটি (শুধু সুপার অ্যাডমিন পারবেন)"); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <h1 className="text-2xl font-bold text-[#1E3A2B]">ওয়েবসাইট সেটিংস</h1>
      <div className="bg-white rounded-2xl border border-stone-200 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="ব্যবসার নাম"><input className={inputCls} value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="set-business-name" /></Field>
        <Field label="লোগো URL"><input className={inputCls} value={form.logo || ""} onChange={(e) => set("logo", e.target.value)} data-testid="set-logo" /></Field>
        <Field label="ফোন ১"><input className={inputCls} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="set-phone" /></Field>
        <Field label="ফোন ২"><input className={inputCls} value={form.phone2 || ""} onChange={(e) => set("phone2", e.target.value)} data-testid="set-phone2" /></Field>
        <Field label="ফোন ৩"><input className={inputCls} value={form.phone3 || ""} onChange={(e) => set("phone3", e.target.value)} data-testid="set-phone3" /></Field>
        <Field label="হোয়াটসঅ্যাপ (8801...)"><input className={inputCls} value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} data-testid="set-whatsapp" /></Field>
        <Field label="ফেসবুক লিংক"><input className={inputCls} value={form.facebook || ""} onChange={(e) => set("facebook", e.target.value)} data-testid="set-facebook" /></Field>
        <Field label="মুদ্রা"><input className={inputCls} value={form.currency || "৳"} onChange={(e) => set("currency", e.target.value)} /></Field>
        <div className="sm:col-span-2"><Field label="ট্যাগলাইন / স্লোগান"><input className={inputCls} value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="set-tagline" /></Field></div>
        <Field label="ঠিকানা"><input className={inputCls} value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="set-address" /></Field>
        <Field label="ডেলিভারি চার্জ (৳)"><input type="number" className={inputCls} value={form.delivery_charge || 0} onChange={(e) => set("delivery_charge", e.target.value)} data-testid="set-delivery" /></Field>
        <Field label="সর্বনিম্ন অর্ডার (৳)"><input type="number" className={inputCls} value={form.min_order || 0} onChange={(e) => set("min_order", e.target.value)} /></Field>
        <div className="sm:col-span-2"><Field label="ব্যবসার বিবরণ (About)"><textarea className={`${inputCls} h-24 py-2`} value={form.business_description || ""} onChange={(e) => set("business_description", e.target.value)} data-testid="set-business-description" /></Field></div>
        <div className="sm:col-span-2"><Field label="ডেলিভারি তথ্য"><textarea className={`${inputCls} h-20 py-2`} value={form.delivery_info || ""} onChange={(e) => set("delivery_info", e.target.value)} data-testid="set-delivery-info" /></Field></div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="font-semibold text-[#1E3A2B] mb-3">পেমেন্ট অ্যাকাউন্ট তথ্য</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="বিকাশ নম্বর"><input className={inputCls} value={form.bkash_number || ""} onChange={(e) => set("bkash_number", e.target.value)} data-testid="set-bkash" /></Field>
          <Field label="নগদ নম্বর"><input className={inputCls} value={form.nagad_number || ""} onChange={(e) => set("nagad_number", e.target.value)} data-testid="set-nagad" /></Field>
          <div className="sm:col-span-2"><Field label="ব্যাংক অ্যাকাউন্ট তথ্য"><input className={inputCls} value={form.bank_info || ""} onChange={(e) => set("bank_info", e.target.value)} data-testid="set-bank" placeholder="নাম, ব্যাংক, শাখা, অ্যাকাউন্ট নম্বর" /></Field></div>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="font-semibold text-[#1E3A2B] mb-3">সক্রিয় পেমেন্ট পদ্ধতি</div>
        <div className="flex flex-wrap gap-2">
          {PMS.map(([id, label]) => (
            <button key={id} onClick={() => togglePM(id)} data-testid={`toggle-pm-${id}`} className={`px-4 h-10 rounded-xl border-2 text-sm font-medium ${(form.payment_methods || []).includes(id) ? "border-[#1E3A2B] bg-emerald-50 text-[#1E3A2B]" : "border-stone-200 text-stone-500"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-[#1E3A2B] flex items-center gap-2"><MapPin size={18} /> এলাকাভেদে ডেলিভারি চার্জ (জেলা/উপজেলা)</div>
          <button onClick={addArea} className="text-sm text-emerald-700 font-medium flex items-center gap-1" data-testid="add-area">
            <Plus size={14} /> এলাকা যোগ
          </button>
        </div>
        <div className="text-xs text-stone-500 mb-3">নির্দিষ্ট জেলা/উপজেলার জন্য আলাদা চার্জ। উপজেলা খালি রাখলে পুরো জেলার জন্য প্রযোজ্য। অগ্রাধিকার: পণ্যভিত্তিক চার্জ &gt; এলাকা চার্জ &gt; গ্লোবাল ডিফল্ট।</div>
        <div className="space-y-2">
          {areas.map((a, i) => {
            const ups = DISTRICTS.find((d) => d.name === a.district)?.upazilas || [];
            return (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`area-row-${i}`}>
                <select className={`${inputCls} col-span-4`} value={a.district || ""} onChange={(e) => setArea(i, "district", e.target.value)} data-testid={`area-district-${i}`}>
                  <option value="">জেলা নির্বাচন</option>
                  {DISTRICTS.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                </select>
                <select className={`${inputCls} col-span-4`} value={a.upazila || ""} onChange={(e) => setArea(i, "upazila", e.target.value)} data-testid={`area-upazila-${i}`}>
                  <option value="">সব উপজেলা</option>
                  {ups.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <input type="number" placeholder="চার্জ ৳" className={`${inputCls} col-span-3`} value={a.charge} onChange={(e) => setArea(i, "charge", e.target.value)} data-testid={`area-charge-${i}`} />
                <button onClick={() => delArea(i)} className="col-span-1 text-red-500" data-testid={`del-area-${i}`}><Trash2 size={16} /></button>
              </div>
            );
          })}
          {areas.length === 0 && <div className="text-sm text-stone-400">কোনো এলাকা চার্জ নেই — নতুন যোগ করুন (না থাকলে গ্লোবাল ডেলিভারি চার্জ প্রযোজ্য হবে)</div>}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-[#1E3A2B] flex items-center gap-2"><Ticket size={18} /> কুপন ব্যবস্থাপনা</div>
          <button onClick={addCoupon} className="text-sm text-emerald-700 font-medium flex items-center gap-1" data-testid="add-coupon"><Plus size={14} /> কুপন যোগ</button>
        </div>
        {coupons.length === 0 && <div className="text-sm text-stone-400">কোনো কুপন নেই — নতুন কুপন যোগ করুন (যেমন EID10 = ১০% ছাড়)</div>}
        <div className="space-y-2">
          {coupons.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`coupon-row-${i}`}>
              <input placeholder="কোড" className={`${inputCls} col-span-3 uppercase`} value={c.code} onChange={(e) => setCoupon(i, "code", e.target.value)} data-testid={`coupon-code-${i}`} />
              <input type="number" placeholder="% ছাড়" className={`${inputCls} col-span-3`} value={c.percent} onChange={(e) => setCoupon(i, "percent", e.target.value)} data-testid={`coupon-percent-${i}`} />
              <input type="number" placeholder="সর্বনিম্ন অর্ডার ৳" className={`${inputCls} col-span-3`} value={c.min_amount || 0} onChange={(e) => setCoupon(i, "min_amount", e.target.value)} />
              <label className="col-span-2 flex items-center gap-1 text-sm"><input type="checkbox" checked={c.active !== false} onChange={(e) => setCoupon(i, "active", e.target.checked)} /> সক্রিয়</label>
              <button onClick={() => delCoupon(i)} className="col-span-1 text-red-500" data-testid={`del-coupon-${i}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="font-semibold text-[#1E3A2B] flex items-center gap-2 mb-1"><MessageSquare size={18} /> SMS নোটিফিকেশন</div>
        <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3">ডেমো মোড (MOCKED) — অর্ডার কনফার্ম ও ডেলিভারিতে গ্রাহককে বাংলা SMS বার্তা তৈরি ও লগ হয়। আসল SMS পাঠাতে প্রোভাইডার (যেমন Twilio) কনফিগার করতে হবে।</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.sms_enabled !== false} onChange={(e) => set("sms_enabled", e.target.checked)} data-testid="sms-enabled" /> SMS চালু</label>
          <Field label="Sender ID"><input className={inputCls} value={form.sms_sender_id || ""} onChange={(e) => set("sms_sender_id", e.target.value)} placeholder="MDRICEMILL" data-testid="sms-sender" /></Field>
        </div>
        {smsLogs.length > 0 && (
          <div className="mt-3">
            <div className="text-sm font-medium text-stone-600 mb-1">সর্বশেষ পাঠানো SMS</div>
            <div className="space-y-1 max-h-48 overflow-auto">
              {smsLogs.slice(0, 10).map((l) => (
                <div key={l.id} className="text-xs bg-stone-50 rounded-lg px-3 py-2" data-testid={`sms-log-${l.id}`}>
                  <div className="font-medium text-stone-700">{l.mobile} • <span className="text-emerald-700">{l.status}</span></div>
                  <div className="text-stone-500">{l.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <button onClick={save} className="bg-[#1E3A2B] text-white h-12 px-6 rounded-xl flex items-center gap-2 font-medium" data-testid="save-settings-btn"><Save size={18} /> সংরক্ষণ করুন</button>
    </div>
  );
}
